<template>
  <v-card class="ma-2">
    <v-card-title class="d-flex align-center position-relative">
      <v-btn
        prepend-icon="mdi-arrow-left"
        variant="plain"
        size="small"
        color="primary"
        class="position-absolute"
        @click="
          $router.push({
            path: '/devops/devops-logs',
            query: {
              startDate: $route.query.startDate,
              endDate: $route.query.endDate,
              search: $route.query.search,
              groupSimilarErrors: $route.query.groupSimilarErrors,
            },
          })
        "
      >
        Back to Error Logs
      </v-btn>
      <div class="d-flex align-center mx-auto">
        <h1 class="d-inline-block mb-0">Error Log Details</h1>
        <EnvironmentTag variant="flat" size="large" class="ml-4" />
      </div>
    </v-card-title>
    <v-card-text>
      <v-row v-if="loading" class="justify-center">
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </v-row>
      <template v-else-if="errorLog">
        <v-row>
          <v-col cols="12">
            <v-card variant="outlined" class="mb-4">
              <v-card-text>
                <div class="text-h6 mb-2">Basic Information</div>
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">Error Log ID</div>
                    <div class="font-weight-bold">{{ $route.params.id }}</div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">Timestamp</div>
                    <div class="font-weight-bold">
                      {{ formatDate(errorLog.created_at) }}
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">File</div>
                    <div class="font-weight-bold">{{ errorLog.file }}</div>
                  </v-col>
                </v-row>
                <v-row class="mt-4">
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">User ID</div>
                    <div class="font-weight-bold">
                      {{ errorLog.user_id || "N/A" }}
                      <span v-if="errorLog.user_name"
                        >({{ errorLog.user_name }})</span
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">Client ID</div>
                    <div class="font-weight-bold">
                      {{ errorLog.client_id || "N/A" }}
                      <span v-if="errorLog.client_name"
                        >({{ errorLog.client_name }})</span
                      >
                    </div>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="text-subtitle-2">Public Site User</div>
                    <div class="font-weight-bold">
                      {{ errorLog.is_public_site_user_profile ? "Yes" : "No" }}
                    </div>
                  </v-col>
                </v-row>
                <v-row class="mt-4">
                  <v-col cols="12">
                    <div class="text-subtitle-2">URL</div>
                    <div class="font-weight-bold">
                      {{ errorLog.url || "N/A" }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-card variant="outlined" class="mb-4">
              <v-card-text>
                <div class="text-h6 mb-2">Error Message</div>
                <div class="error-message">{{ errorLog.message }}</div>
              </v-card-text>
            </v-card>

            <v-card
              v-if="errorLog.request_data && errorLog.request_data !== '[]'"
              variant="outlined"
              class="mb-4"
            >
              <v-card-text>
                <div class="text-h6 mb-2">Request Data</div>
                <pre class="request-data">{{
                  formatJSON(errorLog.request_data)
                }}</pre>
              </v-card-text>
            </v-card>

            <v-card v-if="errorLog.trace" variant="outlined" class="mb-4">
              <v-card-text>
                <div class="text-h6 mb-2">Stack Trace</div>
                <div class="stack-trace-container">
                  <v-table
                    density="compact"
                    class="stack-trace-table"
                    fixed-header
                    height="400"
                  >
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>File</th>
                        <th>Line</th>
                        <th>Function</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="line in parseStackTrace(errorLog.trace)"
                        :key="line.row"
                      >
                        <td>{{ line.row }}</td>
                        <td
                          class="clickable"
                          @click="openInEditor(line.file, line.line)"
                        >
                          <v-icon
                            size="small"
                            color="info"
                            class="mr-1"
                            v-tooltip="line.file"
                          >
                            mdi-information
                          </v-icon>
                          {{ line.file }}
                        </td>
                        <td>{{ line.line }}</td>
                        <td>
                          <v-icon
                            size="small"
                            color="info"
                            class="mr-1"
                            v-tooltip="line.function"
                          >
                            mdi-information
                          </v-icon>
                          {{ line.function }}
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
      <v-row v-else class="justify-center">
        <v-col cols="12" class="text-center">
          <div class="text-h6">Error log not found</div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import EnvironmentTag from "@/components/EnvironmentTag.vue";
import { systemMessages } from "@/mixins/SystemMessages.js";
import axios from "axios";

const API_URL = process.env.VUE_APP_API_URL || "";

export default {
  name: "DevopsErrorLogDetails",
  components: {
    EnvironmentTag,
  },
  mixins: [systemMessages],
  data: () => ({
    environment: process.env.VUE_APP_ENVIRONMENT,
    envName: {
      dev: "local",
      stg: "staging",
      datalake: "datalake",
      uat: "uat",
      prod: "production",
    },
    errorLog: null,
    loading: true,
  }),
  mounted() {
    this.fetchErrorLogDetails();
  },
  methods: {
    async fetchErrorLogDetails() {
      this.loading = true;
      try {
        const response = await axios.get(
          `${API_URL}/error-logs/${this.$route.params.id}`,
          {
            params: {
              environment:
                this.environment === "uat"
                  ? `${this.envName["uat"]},${this.envName["prod"]}`
                  : this.envName[this.environment],
            },
          },
        );
        this.errorLog = response.data;
      } catch (error) {
        this.errorMsg({
          title: "Error",
          message: "Failed to fetch error log details: " + error,
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatJSON(data) {
      try {
        return JSON.stringify(data, null, 2);
      } catch (e) {
        return data;
      }
    },
    parseStackTrace(trace) {
      if (!trace) return [];

      // Split the trace into lines and remove empty lines
      const lines = trace.split("\n").filter((line) => line.trim());

      return lines.map((line) => {
        // Match the PHP stack trace format: #row file(line): function
        const match = line.match(/^#(\d+)\s+(.+?)\((\d+)\):\s+(.+)$/);
        if (match) {
          // Remove /var/www/html from the file path
          const filePath = match[2].replace("/var/www/html/", "");
          return {
            row: match[1],
            file: filePath,
            line: match[3],
            function: match[4],
          };
        }
        return {
          row: "",
          file: line.replace("/var/www/html/", ""),
          line: "",
          function: "",
        };
      });
    },
    openInEditor(file, line) {
      // Detect OS
      const isWindows = navigator.platform.includes("Win");

      // Get the base path from environment or use defaults
      const basePath = isWindows
        ? process.env.VUE_APP_WINDOWS_BASE_PATH || "C:\\Code\\Git Repositories"
        : process.env.VUE_APP_LINUX_BASE_PATH || "/var/www/html";

      // Convert path separators based on OS
      const normalizedPath = isWindows
        ? file.replace(/\//g, "\\")
        : file.replace(/\\/g, "/");

      // Construct the full path
      const fullPath = `${basePath}${isWindows ? "\\" : "/"}${normalizedPath}`;

      // Try different editors in sequence
      const tryOpenInEditor = () => {
        // Try VS Code first
        const vscodeUrl = `vscode://file/${fullPath}:${line}`;
        const vscodeWindow = window.open(vscodeUrl);

        // If VS Code fails, try other editors
        setTimeout(() => {
          if (vscodeWindow && vscodeWindow.closed) {
            // Try Sublime Text
            const sublimeUrl = `subl://${fullPath}:${line}`;
            const sublimeWindow = window.open(sublimeUrl);

            setTimeout(() => {
              if (sublimeWindow && sublimeWindow.closed) {
                // Try Notepad++ on Windows
                if (isWindows) {
                  const notepadUrl = `notepad++://${fullPath}:${line}`;
                  window.open(notepadUrl);
                } else {
                  // Try gedit on Linux
                  const geditUrl = `gedit://${fullPath}:${line}`;
                  window.open(geditUrl);
                }
              }
            }, 500);
          }
        }, 500);
      };

      tryOpenInEditor();
    },
  },
};
</script>

<style scoped>
.loading-dots {
  display: flex;
  gap: 4px;
  padding: 20px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background-color: #1976d2;
  border-radius: 50%;
  animation: bounce 0.5s infinite alternate;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-10px);
  }
}

.error-message {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
}

.stack-trace-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.stack-trace-table {
  font-family: monospace;
  font-size: 0.75rem;
}

.stack-trace-table :deep(th) {
  font-weight: 600;
  background-color: #f5f5f5;
  text-align: left;
  padding: 4px 4px !important;
}

.stack-trace-table :deep(td) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  padding: 4px 4px !important;
  text-align: left;
}

.stack-trace-table :deep(td:first-child) {
  width: 30px;
  text-align: left;
  padding-right: 0 !important;
}

.stack-trace-table :deep(td:nth-child(3)) {
  width: 40px;
  text-align: left;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.stack-trace-table :deep(td:nth-child(2)) {
  max-width: 400px;
  padding-left: 4px !important;
}

.stack-trace-table :deep(td:nth-child(4)) {
  max-width: 300px;
  padding-left: 4px !important;
}

.stack-trace-table :deep(.v-table__wrapper table) {
  border-spacing: 0;
  border-collapse: collapse;
}

.stack-trace-table :deep(.v-table__wrapper table td),
.stack-trace-table :deep(.v-table__wrapper table th) {
  padding: 4px 4px !important;
}

.stack-trace-table :deep(td.clickable) {
  cursor: pointer;
  color: #1976d2;
}

.stack-trace-table :deep(td.clickable:hover) {
  text-decoration: underline;
}

.request-data {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  background-color: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  max-height: 400px;
  overflow-y: auto;
}
</style>
